import { analyticsService } from '@common/analytics';
import {
  continueChatApi, createChatApi, getPricingOptions, hangupChatApi
} from '@common/api/api';
import Const from '../config/const';
import { purchaseDetailsTypes, topupFlows } from '../../common/config/const';
import {
  toCamelCase, getDataFromStorage, paramOrNul, getClientPlatform, getErrorMessage, noInternetConnectionError
} from '../../common/config/utils';
import { log, LOG_LEVELS } from '../../common/config/app_logger';
import {
  StatusBusy, StatusHangUp,
  getFlow,
  StatusSuccess,
  startChatProperties,
  trackPaygRelatedProps,
  getSessionType,
  getSessionEventsProps,
  parseAndTrackAnalyticEvents,
  StatusError
} from './analytics';
import {
  getDurationPickRelatedProps, isPayg, getDefaultDuration, getDurationOptions
} from '../config/util';
import { updatePurchaseDetails, clearPurchaseDetails } from './purchase_details';
import { updateFxRate } from './application';
import { loadAdvisor } from './advisors';
import { getAdvisorSlug } from '../project_items/helper_functions';
import { getBaseProfileProperties } from './mixpanel_properties';

export const valiateUser = (user) => {
  const { gender, dateOfBirth, nickname } = user;
  return !(gender && dateOfBirth && nickname);
};

const continueChatActionCreator = (duration, price) => ({
  type: Const.chat.continueChat,
  payload: {
    duration,
    price
  }
});

export const pricingLoading = () => ({
  type: Const.chat.pricingLoading
});

export const trackEventProfileButton = (conversionEventId) => () => {
  analyticsService.trackEvent('achievement unlocked', {}, { conversionEventId, isCustomEvent: true });
};

export const loadPricingOptions = (type, advisorId) => (dispatch, getState) => {
  dispatch(pricingLoading());
  const { chat: { requestParams } } = getState();
  getPricingOptions(advisorId, { liveModes: type, ...requestParams }).then((res) => {
    const {
      chatPricingOptions, conversionEventId, selectDurationKind, user, newUserPaygPricingOption, texts, fxRate, currency
    } = res;
    if (fxRate != null) dispatch(updateFxRate(fxRate));
    if (conversionEventId) trackEventProfileButton(conversionEventId);
    dispatch({
      type: Const.chat.start,
      advisorId,
      pricingOptions: chatPricingOptions || [],
      selectDurationKind,
      xfmProgram: user?.xfmProgram,
      newUserPaygPricingOption,
      texts,
      fxRate,
      currency
    });
  }).catch((error) => {
    log(LOG_LEVELS.ERROR, 'Chat', 'Error loading pricing options', error);
    getErrorMessage(error).then(errorMessage => {
      if (errorMessage === noInternetConnectionError) return;
      dispatch({ type: Const.chat.cantCreate, errors: [errorMessage] });
    });
  });
};

export const chatInit = (
  advisorId,
  name,
  profilePictureUrl,
  pricePerMinute,
  startChatClickSource,
  requestParams,
  fxRate,
  currency
) => dispatch => {
  dispatch({
    type: Const.chat.init,
    advisorId,
    name,
    profilePictureUrl,
    pricePerMinute,
    startChatClickSource,
    requestParams,
    fxRate,
    currency
  });
};

export const startChat = (
  advisorId,
  pricePerMinute,
  profilePictureUrl,
  name,
  startChatClickSource,
  homepageSection,
  requestParams,
  fxRate,
  currency
) => ({
  type: Const.chat.start,
  advisorId,
  pricePerMinute,
  profilePictureUrl,
  name,
  startChatClickSource,
  homepageSection,
  requestParams,
  fxRate,
  currency
});

const doContinueChat = (orderId, duration, dispatch) => (continueChatApi(orderId, duration)
  .then(success => {
    log(LOG_LEVELS.INFO, 'Chat', 'everything is fine, chat should be continued now', success);
  })
  .catch(error => {
    // TODO: handle 404 - advisor is not in a live mode
    switch (error.status) {
      case 422: {
        error.json().then(jsonError => {
          const { fullMessages } = toCamelCase(jsonError);
          dispatch({
            type: Const.chat.generalError,
            errors: fullMessages
          });
        });
        break;
      }
      default: {
        dispatch({ type: Const.chat.unknownError, error: error.json() });
        log(LOG_LEVELS.ERROR, 'Chat', 'Error continuing the chat', error);
      }
    }
  })
);

const purchaseBack = continueFlow => ({
  type: Const.chat.purchaseBack,
  payload: {
    continueFlow
  }
});

const continueChat = (duration) => (dispatch, getState) => {
  const {
    chat: { orderId, pricingOptions }
  } = getState();

  const item = pricingOptions.find((i) => i.duration === duration);

  // Duration and Pirce need to be updated here in order to track
  // correct properties in chat started event
  dispatch(continueChatActionCreator(duration, item.price));
  doContinueChat(orderId, duration, dispatch);
};

export const purchaseOnBack = () => (dispatch, getState) => {
  const { chat: { orderId } } = getState();
  dispatch(purchaseBack(!!orderId));
  dispatch(clearPurchaseDetails());
};

const continueCall = (dispatch, getState) => {
  const { chat: { duration } } = getState();
  dispatch(continueChat(duration));
};

export const purchaseSuccess = () => (dispatch, getState) => {
  const { user: { user }, chat: { orderId } } = getState();

  if (orderId) {
    continueCall(dispatch, getState);
    return;
  }

  dispatch({
    type: Const.chat.paymentComplete,
    introduce: valiateUser(user)
  });
};

const durationSelected = ({
  duration, price, selectedCarouselIndex, user, hasEnoughtCredit, isContinue, isNewUser, flow
}) => ({
  type: Const.chat.durationSelected,
  duration,
  price,
  selectedCarouselIndex,
  introduce: valiateUser(user),
  isPayg: isPayg({ duration }),
  paymentRequired: !hasEnoughtCredit,
  onContinue: isContinue,
  isNewUser,
  flow
});

const trackDurationPickRelatedAction = ({
  option, time, defaultDurationChanged, topupFlow, mpEvent
}) => {
  const {
    price, localPrice, duration, payAsYouGo, additional
  } = option;
  const {
    user: { user: { localAvailableCredit } },
    chat: {
      advisorId, pricingOptions, selectDurationKind,
      continuedSession, homepageSection, startChatClickSource
    }
  } = window.store.getState();
  const displayVersion = selectDurationKind === Const.selectDurationKind.newUser ? 'version 1' : 'original';
  const defaultDuration = getDefaultDuration({ pricingOptions, payAsYouGo });
  const durationOptions = getDurationOptions({ pricingOptions, payAsYouGo });
  const effectivePrice = localPrice;
  const hasEnoughtCredit = localAvailableCredit >= effectivePrice;
  const requiredAmount = hasEnoughtCredit ? null : { 'purchase required': (effectivePrice - localAvailableCredit).toFixed(2) };
  const flow = getFlow({ additional, payAsYouGo });
  const durationPickRelatedProps = getDurationPickRelatedProps({
    requiredAmount, continuedSession, selectedDuration: duration, price, advisorId,
    homepageSection, startChatClickSource, displayVersion, defaultDuration, defaultDurationChanged,
    durationOptions, time, payAsYouGo, flow, additional, topupFlow
  });
  mpEvent(Const.chatType.text, durationPickRelatedProps);
};

export const trackDurationPickExitMPEvent = (chatType, payload) => {
  analyticsService.trackEvent(`${ getSessionType(chatType) } duration pick exit`, payload);
};

export const trackDurationPickedMPEvent = (chatType, payload) => {
  const properties = {
    ...payload,
    ...paramOrNul('urgency sentence string', getDataFromStorage('advisor_urgency_signal_text'))
  };
  let eventName = '';
  switch (chatType) {
    case Const.chatType.text:
      eventName = 'chat duration picked';
      break;
    case Const.chatType.voice:
      eventName = 'voice duration picked';
      break;
    case Const.chatType.video:
      eventName = 'vc duration picked';
      break;
    default:
      break;
  }
  analyticsService.trackEvent(eventName, properties);
};

export const trackDurationPicked = (option, time, defaultDurationChanged, topupFlow) => {
  trackDurationPickRelatedAction({
    option, time, defaultDurationChanged, mpEvent: trackDurationPickedMPEvent, topupFlow
  });
};

export const trackDurationPickExit = (option, time, defaultDurationChanged) => {
  trackDurationPickRelatedAction({
    option, time, defaultDurationChanged, mpEvent: trackDurationPickExitMPEvent
  });
};

export const selectCredit = (option, time, defaultDurationChanged, isContinue) => (dispatch, getState) => {
  const {
    price, localPrice, duration, selectedCarouselIndex, additional
  } = option;
  const {
    user: { user, user: { localAvailableCredit } },
    chat: { advisorId }
  } = getState();
  const effectivePrice = localPrice;
  const hasEnoughtCredit = localAvailableCredit >= effectivePrice;
  trackDurationPicked(option, time, defaultDurationChanged, isContinue ? topupFlows.regular : undefined);
  if (hasEnoughtCredit && isContinue) {
    dispatch(continueChat(duration));
    return;
  }
  const flow = getFlow({ additional, payAsYouGo: isPayg({ duration }) });
  dispatch(updatePurchaseDetails({
    clickSource: 'live chat', purchaseType: purchaseDetailsTypes.CHAT, topupFlow: isContinue ? topupFlows.regular : undefined, details: {
      advisorId, duration, price, isPayg: isPayg({ duration }), flow
    }
  }));
  dispatch(durationSelected({
    duration, price, selectedCarouselIndex, user, hasEnoughtCredit, isContinue, option, isPayg: isPayg({ duration }), flow
  }));
};

export const selectDurationOption = (option, isContinue, isNewUser, index) => (dispatch, getState) => {
  const {
    price, localPrice, duration, additional
  } = option;
  const { user: { user }, user: { localAvailableCredit } } = getState();
  const effectivePrice = localPrice;
  const hasEnoughtCredit = localAvailableCredit >= effectivePrice;
  const flow = getFlow({ additional, payAsYouGo: isPayg({ duration }) });
  dispatch(durationSelected({
    duration,
    price,
    selectedCarouselIndex: index,
    user,
    hasEnoughtCredit,
    isContinue,
    option,
    isNewUser,
    flow
  }));
};

export const cancelChat = () => ({
  type: Const.chat.cancel
});

export const clearSumamryScreen = () => ({
  type: Const.chat.clearSummaryScreen
});

export const trackChatStartedEvent = (chatType, status, chatData, user, errorMessage) => {
  const { analytics } = chatData;
  const props = getSessionEventsProps(chatType, status, chatData, user, analytics);
  analyticsService.trackEvent(`${ getSessionType(chatType) } started`, {
    ...props,
    platform: getClientPlatform(),
    ...paramOrNul('error message', errorMessage)
  });
};

const trackMPBusyEventIdNeeded = (fullMessages, getState) => {
  const { chat, user: { user } } = getState();
  fullMessages.forEach(element => {
    if (element.includes(StatusBusy)) {
      trackChatStartedEvent(Const.chatType.text, StatusBusy, chat, user);
    } else {
      trackChatStartedEvent(Const.chatType.text, StatusError, chat, user, element);
    }
  });
};

const trackChatAttemptEvent = () => {
  const {
    chat
  } = window.store.getState();

  const {
    initialDuration, initialCharged, analytics, initialVoipCharged, initialVoipDuration, isPayg: isPAYG
  } = chat;

  const eventName = 'chat attempt';
  const chatProps = {
    ...startChatProperties(chat, 'chat', analytics),
    ...paramOrNul('chat initial minutes', trackPaygRelatedProps(isPAYG, (initialDuration || initialVoipDuration) / 60)),
    ...paramOrNul('chat initial credit', trackPaygRelatedProps(isPAYG, initialCharged || initialVoipCharged)),
    platform: getClientPlatform()
  };

  analyticsService.trackEvent(eventName, chatProps);
};

export const createChat = () => (dispatch, getState) => {
  dispatch({
    type: Const.chat.connect
  });
  const {
    chat: {
      pricePerMinute, duration, advisorId, startChatClickSource, homepageSection, fxRate, currency
    }
  } = getState();
  trackChatAttemptEvent();
  const urgencySignalText = getDataFromStorage('advisor_urgency_signal_text');
  createChatApi(pricePerMinute, duration, advisorId, startChatClickSource, urgencySignalText, homepageSection, fxRate, currency)
    .then(config => {
      dispatch({
        type: Const.chat.connectProvider,
        config
      });
    })
    .catch(error => {
      if (!window.currentUserId) {
        log(LOG_LEVELS.INFO, 'CHAT', 'Ignoring create chat error - user logged out');
        dispatch({ type: Const.chat.cancel });
        return;
      }
      switch (error.status) {
        case 404: {
          dispatch({
            type: Const.chat.notInLiveMode,
            error: 'Advisor is not in live mode anyomre'
          });
          break;
        }
        case 422: {
          error.json().then(jsonError => {
            const { fullMessages } = toCamelCase(jsonError);
            trackMPBusyEventIdNeeded(fullMessages, getState);
            dispatch({
              type: Const.chat.cantCreate,
              errors: fullMessages
            });
          });
          break;
        }
        default: {
          dispatch({ type: Const.chat.unknownError, error: error.json() });
        }
      }
    });
};

const processAdminMessageIfNeed = (adminMessage) => (dispatch, getState) => {
  if (!adminMessage) return;
  const { chat: { advisorId, state: chatState } } = getState();
  if (chatState === null) return;
  const { state: messageState, reason, ...details } = adminMessage;
  if (advisorId) {
    dispatch(loadAdvisor(getAdvisorSlug({ advisorId })));
  }
  dispatch({
    type: Const.chat.adminUpdate, state: messageState, details: toCamelCase(details), reason
  });
};

export const hangupChat = () => (dispatch, getState) => {
  dispatch({
    type: Const.chat.hangingUp
  });
  const { orderId, state } = getState().chat;
  // We need to keep the old state, because we need data from the old state
  // like duration, ppm etc. When the API request finishes then the state will be a fresh state,
  // so we can't track the MP event correctly
  const { chat, user: { user } } = getState();
  hangupChatApi(orderId).then((response) => {
    if (state === Const.chatStates.ringing) {
      trackChatStartedEvent(Const.chatType.text, StatusHangUp, chat, user, dispatch);
    }
    dispatch(processAdminMessageIfNeed(response.adminMessage));
    log(LOG_LEVELS.INFO, 'Chat', 'is supposed to be ended');
  });
};

export const cancelChatOnPPActions = (isContinue) => ({
  type: Const.chat.cancelOnPP,
  isContinue
});

export const setNeedTrackSessionHireEventAvtion = (value) => ({
  type: Const.chat.setNeedTrackSessionHireEvent,
  value
});

export const trackSessionHireEventIfNeeded = () => {
  const { chat, user: { user } } = window.store.getState();
  const { needTrackSessionHireEvent } = chat;

  if (!needTrackSessionHireEvent) return;

  const { analytics, manualAnalyticEvents } = chat;
  const props = getSessionEventsProps(Const.chatType.text, StatusSuccess, chat, user, analytics);

  analyticsService.trackEvent('session hire', props);
  parseAndTrackAnalyticEvents(manualAnalyticEvents, analytics);

  window.store.dispatch(setNeedTrackSessionHireEventAvtion(false));
};

export const hideTopUpBanner = () => ({
  type: Const.topup.hide
});

export const changeTopUpState = (state) => ({
  type: Const.topup.changeState,
  state
});

export const setTopupPaidMinutes = (duration) => ({
  type: Const.chat.topupPaidMinutes,
  duration
});

export const trackQuickTopupBannerViewed = (selection, duration, timeToClick) => {
  const { chat } = window.store.getState();
  const props = {
    ...startChatProperties(chat, getSessionType(Const.chatType.text), chat.analytics),
    selection,
    'quick top up duration': duration,
    'time to click': timeToClick
  };

  analyticsService.trackEvent('quick topup banner viewed', props);
};

export const trackEventSpentCredit = (price, userId, orderId) => {
  analyticsService.trackEvent('spent credits', { price }, { conversionEventId: `${ userId }${ orderId }` });
};

export const trackChatEndedEvent = ({
  chatType, advisorId, userId, orderId, totalCharged, totalDuration
}) => {
  const advisorAnalytics = window.store.getState().advisors[advisorId]?.analytics;
  const customerAnalytics = window.store.getState().user?.user?.analytics;
  const properties = {
    'advisor id': advisorId,
    'user id': userId,
    'order id': orderId,
    'total charged': parseFloat(totalCharged).toFixed(2),
    'total duration': parseFloat(totalDuration / 60).toFixed(2),
    ...toCamelCase(getBaseProfileProperties()),
    ...advisorAnalytics,
    ...customerAnalytics
  };
  analyticsService.trackEvent(`${ getSessionType(chatType) } ended`, properties);
};

const calculateMessageCounts = ()  => {
  const { chat, pubnub: { ownUuid } } = window.store.getState();
  const { messages } = chat;
  if (!messages || !ownUuid) return { clientMessageCount: null, advisorMessageCount: null };
  const clientMessages = messages.filter(
    m => m.kind === Const.message.kinds.text && parseInt(m.uuid, 10) === parseInt(ownUuid, 10)
  );
  const advisorMessages = messages.filter(
    m => m.kind === Const.message.kinds.text && parseInt(m.uuid, 10) !== parseInt(ownUuid, 10)
  );
  return {
    clientMessageCount: clientMessages.length,
    advisorMessageCount: advisorMessages.length
  };
};

export const trackSessionSummaryEvent = (properties) => {
  const eventName = 'session summary';
  const { orderId, sessionType, mbwChatAnalytics = {} } = properties;
  const sessionProps = {
    'order id': orderId,
    source: 'buyer',
    'session type': getSessionType(sessionType),
    ...paramOrNul('buyer message count', sessionType === Const.chatType.text ? calculateMessageCounts()?.clientMessageCount : null),
    ...paramOrNul('advisor message count', sessionType === Const.chatType.text ? calculateMessageCounts()?.advisorMessageCount : null),
    ...mbwChatAnalytics
  };
  analyticsService.trackEvent(eventName, sessionProps);
};
